import type { PersonSummary } from '@sentra/contracts';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/design/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/design/components/dialog';

/**
 * AD-53: a record action reached by SEARCH is acknowledged before it happens.
 *
 * **The point is the mark on the audit row, not the click.** Every one of these actions records
 * `method: manual_search`, and the manual-versus-scan ratio per operator is a dashboard metric — so
 * what makes the metric mean anything is that reaching a record without scanning a pass is a
 * deliberate act somebody confirmed, rather than the same action taken two ways with one of them
 * unlabelled.
 *
 * **Cancelling must send NOTHING.** That is the assertion this dialog exists to make true: an
 * acknowledgment somebody declined has to leave no trace at all, because a request issued anyway
 * would put a `manual_search` row in the trail for an action nobody took — and on `person.get` that
 * row is a `person.view_full`, which AD-37's exfiltration threshold counts.
 *
 * **The person is named by their short reference and nothing else.** The operator is choosing from a
 * list they are already looking at, so there is nothing to add; and a dialog that repeated a name
 * would be a name in a screenshot somebody takes of a confirmation box.
 */

/** What the operator is about to do, which decides the sentence they are shown. */
export type ManualAction = 'modify' | 'presence' | 'shred' | 'view';

export function AcknowledgmentDialog({
  pending,
  onConfirm,
  onCancel,
}: Readonly<{
  /** The action awaiting acknowledgment, or `null` when there is none. */
  pending: { readonly action: ManualAction; readonly person: PersonSummary } | null;
  onConfirm: () => void;
  onCancel: () => void;
}>): ReactNode {
  const { t } = useTranslation();

  function describe(action: ManualAction): string {
    switch (action) {
      case 'view': {
        return t('person.table.ackView');
      }
      case 'modify': {
        return t('person.table.ackModify');
      }
      case 'presence': {
        return t('person.table.ackPresence');
      }
      case 'shred': {
        return t('person.table.ackShred');
      }
    }
  }

  return (
    <Dialog
      open={pending !== null}
      onOpenChange={(next) => {
        if (!next) {
          onCancel();
        }
      }}
    >
      <DialogContent closeLabel={t('common.close')} data-testid="acknowledgment-dialog">
        <DialogHeader>
          <DialogTitle>{t('person.table.ackTitle')}</DialogTitle>
          <DialogDescription>{pending === null ? '' : describe(pending.action)}</DialogDescription>
        </DialogHeader>

        <p className="text-sm">
          {t('person.table.ackRecord', { shortRef: pending?.person.shortRef ?? '' })}
        </p>
        <p className="text-sm text-muted-foreground">{t('person.table.ackWhy')}</p>

        <DialogFooter>
          <Button type="button" variant="outline" data-testid="ack-cancel" onClick={onCancel}>
            {t('person.table.ackCancel')}
          </Button>
          <Button type="button" data-testid="ack-confirm" onClick={onConfirm}>
            {t('person.table.ackConfirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
